import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FormField,
  FormFieldValidationRule,
  LocalizedText,
  PATTERN_PRESETS,
  VALIDATION_RULE_TYPES,
  ValidationRuleType,
  readLocalizedText,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';

interface SelectOption<T = string> {
  label: string;
  value: T;
}

@Component({
  standalone: true,
  selector: 'ain-form-builder-validation-panel',
  imports: [FormsModule, ButtonModule, InputTextModule, InputNumberModule, SelectModule, AinFormFieldComponent, InfoHintComponent],
  template: `
    <section class="logic-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.validation.infoTitle'"
        [descriptionKey]="'dynamic.builder.validation.infoDescription'"
        [bulletKeys]="['dynamic.builder.validation.infoBullet']" />

      <div class="toolbar">
        <strong>{{ t('dynamic.builder.validation.title') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.validation.addRule')" (click)="addRule()"></button>
        }
      </div>

      @for (rule of rules(); track $index; let idx = $index) {
        <article class="rule-card">
          <ain-form-field [label]="t('dynamic.builder.validation.ruleType')">
            <p-select
              [options]="ruleTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="rule.rule"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateRuleType(idx, $event)" />
          </ain-form-field>

          @if (needsNumericValue(rule.rule)) {
            <div class="value-grid">
              @if (rule.rule === 'range' || rule.rule === 'min' || rule.rule === 'max') {
                <ain-form-field [label]="t('dynamic.builder.validation.min')">
                  <p-inputNumber
                    [ngModel]="readRangeValue(rule, 'min')"
                    [disabled]="!canEdit()"
                    [useGrouping]="false"
                    (ngModelChange)="updateRangeValue(idx, 'min', $event)" />
                </ain-form-field>
                <ain-form-field [label]="t('dynamic.builder.validation.max')">
                  <p-inputNumber
                    [ngModel]="readRangeValue(rule, 'max')"
                    [disabled]="!canEdit()"
                    [useGrouping]="false"
                    (ngModelChange)="updateRangeValue(idx, 'max', $event)" />
                </ain-form-field>
              } @else {
                <ain-form-field [label]="t('dynamic.builder.validation.value')">
                  <p-inputNumber
                    [ngModel]="readScalarValue(rule)"
                    [disabled]="!canEdit()"
                    [min]="1"
                    [useGrouping]="false"
                    (ngModelChange)="updateScalarValue(idx, $event)" />
                </ain-form-field>
              }
            </div>
          }

          @if (rule.rule === 'pattern') {
            <ain-form-field [label]="t('dynamic.builder.validation.patternPreset')">
              <p-select
                [options]="patternPresetOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="readPatternPreset(rule)"
                [disabled]="!canEdit()"
                appendTo="body"
                (ngModelChange)="applyPatternPreset(idx, $event)" />
            </ain-form-field>
            <ain-form-field [label]="t('dynamic.builder.validation.pattern')">
              <input
                pInputText
                [ngModel]="readPatternValue(rule)"
                [disabled]="!canEdit()"
                (ngModelChange)="updatePatternValue(idx, $event)" />
            </ain-form-field>
            <small class="hint">{{ t('dynamic.builder.validation.patternHint') }}</small>
          }

          <ain-form-field [label]="t('dynamic.builder.validation.errorMessage')">
            <input
              pInputText
              [ngModel]="readErrorMessage(rule)"
              [disabled]="!canEdit()"
              (ngModelChange)="updateErrorMessage(idx, $event)" />
          </ain-form-field>

          @if (canEdit()) {
            <button pButton type="button" severity="danger" text icon="pi pi-trash" [label]="t('dynamic.builder.validation.removeRule')" (click)="removeRule(idx)"></button>
          }
        </article>
      }

      @if (!rules().length) {
        <p class="empty">{{ t('dynamic.builder.validation.empty') }}</p>
      }
    </section>
  `,
  styles: [`
    .logic-panel { display: grid; gap: 10px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .rule-card {
      display: grid;
      gap: 8px;
      padding: 10px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
    }
    .value-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .empty, .hint { margin: 0; color: #64748b; font-size: 0.875rem; }
  `]
})
export class FormBuilderValidationPanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly field = input.required<FormField>();
  readonly culture = input.required<string>();
  readonly canEdit = input(true);

  readonly fieldChange = output<FormField>();

  readonly rules = computed(() =>
    (this.field().validation ?? []).filter(rule => rule.rule !== '_builderMeta')
  );

  readonly ruleTypeOptions = computed<SelectOption<ValidationRuleType>[]>(() =>
    VALIDATION_RULE_TYPES.map(value => ({
      value,
      label: this.t(`dynamic.builder.validation.rule.${value}`)
    }))
  );

  readonly patternPresetOptions = computed(() => [
    { label: this.t('dynamic.builder.validation.patternCustom'), value: 'custom' },
    ...PATTERN_PRESETS.map(preset => ({
      label: this.t(`dynamic.builder.validation.patternPreset.${preset.key}`),
      value: preset.key
    }))
  ]);

  needsNumericValue(rule: string): boolean {
    return ['minlength', 'maxlength', 'min', 'max', 'range'].includes(rule);
  }

  readScalarValue(rule: FormFieldValidationRule): number | null {
    if (typeof rule.value === 'number') return rule.value;
    if (typeof rule.value === 'object' && rule.value) {
      const key = rule.rule === 'minlength' ? 'minLength' : 'maxLength';
      const value = (rule.value as Record<string, unknown>)[key];
      return typeof value === 'number' ? value : null;
    }
    return null;
  }

  readRangeValue(rule: FormFieldValidationRule, bound: 'min' | 'max'): number | null {
    if (typeof rule.value === 'object' && rule.value) {
      const value = (rule.value as Record<string, unknown>)[bound];
      return typeof value === 'number' ? value : null;
    }
    if (bound === 'min' && rule.rule === 'min' && typeof rule.value === 'number') return rule.value;
    if (bound === 'max' && rule.rule === 'max' && typeof rule.value === 'number') return rule.value;
    return null;
  }

  readPatternValue(rule: FormFieldValidationRule): string {
    if (typeof rule.value === 'string') return rule.value;
    if (typeof rule.value === 'object' && rule.value) {
      const pattern = (rule.value as Record<string, unknown>)['pattern'];
      return typeof pattern === 'string' ? pattern : '';
    }
    return '';
  }

  readPatternPreset(rule: FormFieldValidationRule): string {
    const pattern = this.readPatternValue(rule);
    const match = PATTERN_PRESETS.find(preset => preset.pattern === pattern);
    return match?.key ?? 'custom';
  }

  readErrorMessage(rule: FormFieldValidationRule): string {
    if (typeof rule.value === 'object' && rule.value) {
      const message = (rule.value as Record<string, unknown>)['message'];
      if (typeof message === 'string') return message;
      if (typeof message === 'object' && message) {
        return readLocalizedText(message as Record<string, string>, this.editCultureService.editCulture());
      }
    }
    return '';
  }

  addRule(): void {
    this.emitRules([...(this.rules()), { rule: 'email' }]);
  }

  removeRule(index: number): void {
    this.emitRules(this.rules().filter((_, idx) => idx !== index));
  }

  updateRuleType(index: number, ruleType: ValidationRuleType): void {
    const next = [...this.rules()];
    next[index] = { rule: ruleType, value: this.defaultValueForRule(ruleType) };
    this.emitRules(next);
  }

  updateScalarValue(index: number, value: number | null): void {
    const rule = this.rules()[index];
    if (!rule) return;
    const next = [...this.rules()];
    const key = rule.rule === 'minlength' ? 'minLength' : 'maxLength';
    next[index] = { rule: rule.rule, value: { [key]: value ?? 0, message: this.readErrorMessage(rule) || undefined } };
    this.emitRules(next);
  }

  updateRangeValue(index: number, bound: 'min' | 'max', value: number | null): void {
    const rule = this.rules()[index];
    if (!rule) return;
    const current: Record<string, unknown> = typeof rule.value === 'object' && rule.value
      ? { ...(rule.value as Record<string, unknown>) }
      : {};
    current[bound] = value ?? undefined;
    const message = this.readErrorMessage(rule);
    if (message) {
      current['message'] = message;
    }
    const next = [...this.rules()];
    next[index] = { rule: rule.rule, value: current };
    this.emitRules(next);
  }

  updatePatternValue(index: number, pattern: string): void {
    const rule = this.rules()[index];
    if (!rule) return;
    const next = [...this.rules()];
    next[index] = {
      rule: 'pattern',
      value: { pattern, message: this.readErrorMessage(rule) || undefined }
    };
    this.emitRules(next);
  }

  applyPatternPreset(index: number, presetKey: string): void {
    if (presetKey === 'custom') return;
    const preset = PATTERN_PRESETS.find(item => item.key === presetKey);
    if (preset) {
      this.updatePatternValue(index, preset.pattern);
    }
  }

  updateErrorMessage(index: number, message: string): void {
    const rule = this.rules()[index];
    if (!rule) return;
    const next = [...this.rules()];
    const value: Record<string, unknown> = typeof rule.value === 'object' && rule.value
      ? { ...(rule.value as Record<string, unknown>) }
      : rule.value !== undefined
        ? { value: rule.value }
        : {};
    value['message'] = writeLocalizedText({}, this.editCultureService.editCulture(), message);
    next[index] = { rule: rule.rule, value };
    this.emitRules(next);
  }

  private defaultValueForRule(ruleType: ValidationRuleType): unknown {
    switch (ruleType) {
      case 'minlength':
        return { minLength: 1 };
      case 'maxlength':
        return { maxLength: 255 };
      case 'pattern':
        return { pattern: '^.*$' };
      case 'range':
        return { min: 0, max: 100 };
      case 'min':
        return { min: 0 };
      case 'max':
        return { max: 100 };
      default:
        return undefined;
    }
  }

  private emitRules(rules: FormFieldValidationRule[]): void {
    this.fieldChange.emit({
      ...this.field(),
      validation: rules.length ? rules : undefined
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
